import { createLogger } from '@mail-core/logger/src/octologger/logger/logger';
import type { RootLogger } from '@mail-core/logger/src/octologger/logger/logger.types';
import { universalOutput } from '@mail-core/logger/src/octologger/output/output';

type LogArgs = [...unknown[]];

type LoggerMethodMap = {
    ok(...msg: LogArgs): any;
    info(...msg: LogArgs): any;
    error(...msg: LogArgs): any;
};

const createPlatformLogger = (DEBUG_SCOPE: string) =>
    createLogger<LoggerMethodMap>(
        // Options
        {
            output: universalOutput(),
        },

        // Methods
        ({ logger, createEntry }) => ({
            ok(stage, ...msg) {
                logger.add(createEntry('done', '✅ ', `${DEBUG_SCOPE}:OK`, [`[${stage}]`, ...msg]));
            },

            info(stage, ...msg) {
                logger.add(createEntry('info', '👀 ', `${DEBUG_SCOPE}:INFO`, [`[${stage}]`, ...msg]));
            },

            error(stage, ...msg) {
                logger.add(createEntry('error', '❌ ', `${DEBUG_SCOPE}:ERROR`, [`[${stage}]`, ...msg]));
            },
        })
    );

export type DebugLoggerOptions = {
    /** Префикс ко всем логам */
    readonly DEBUG_SCOPE: string;
    /** Писать ли лог */
    DEBUG?: boolean;
};

export class DebugLogger implements LoggerMethodMap {
    private static instances: Set<DebugLogger> = new Set();

    private readonly scopedLogger: RootLogger<LoggerMethodMap>;

    constructor(options: DebugLoggerOptions) {
        this.scopedLogger = createPlatformLogger(options.DEBUG_SCOPE);

        this.scopedLogger.setup({
            silent: !options.DEBUG,
        });

        DebugLogger.instances.add(this);
    }

    public info(...args: LogArgs) {
        return this.scopedLogger.info(...args);
    }

    public error(...args: LogArgs) {
        return this.scopedLogger.error(...args);
    }

    public ok(...args: LogArgs) {
        return this.scopedLogger.ok(...args);
    }

    public switch(isOk, ...args: LogArgs) {
        if (isOk) {
            return this.scopedLogger.ok(...args);
        }
        return this.scopedLogger.error(...args);
    }

    static setDebug(DEBUG: boolean) {
        DebugLogger.instances.forEach((logger) => {
            logger.scopedLogger.setup({
                silent: !DEBUG,
            });
        });
    }
}
